import React, { useEffect, useState, useRef } from "react";
import { toast } from "sonner";
import api, { apiErr, API } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Field, Input, Textarea } from "@/components/Field";
import { Switch } from "@/components/ui/switch";
import { applyFavicon } from "@/lib/favicon";
import GoogleOAuthSection from "./GoogleOAuthSection";
import AIProviderConfig from "./AIProviderConfig";

const Section = ({ title, children }) => (
  <div className="surface p-6 space-y-4"><h2 className="font-display font-bold text-lg">{title}</h2>{children}</div>
);

export default function Settings() {
  const { t, lang } = useI18n();
  const [s, setS] = useState(null);
  const [status, setStatus] = useState(null);
  const [emailLog, setEmailLog] = useState([]);
  const favInput = useRef();
  const ogInput = useRef();
  useEffect(() => {
    api.get("/settings").then((r) => setS(r.data)).catch(() => {});
    api.get("/system/status").then((r) => setStatus(r.data)).catch(() => {});
    api.get("/email-log").then((r) => setEmailLog(r.data)).catch(() => {});
  }, []);
  const testEmail = async () => {
    try { const { data } = await api.post("/email/test", { lang }); toast.success(`E-Mail: ${data.status}`); api.get("/email-log").then((r) => setEmailLog(r.data)); }
    catch (e) { toast.error(apiErr(e)); }
  };
  if (!s) return <div className="text-muted-foreground">{t("common.loading")}</div>;
  const set = (k, v) => setS({ ...s, [k]: v });
  const setStat = (k, v) => setS({ ...s, stats: { ...(s.stats || {}), [k]: Number(v) } });
  const setSocial = (plat, patch) => setS({ ...s, social_media: { ...(s.social_media || {}), [plat]: { ...((s.social_media || {})[plat] || {}), ...patch } } });
  const save = async () => {
    const sm = s.social_media || {};
    for (const [key, label] of [["facebook", "Facebook"], ["instagram", "Instagram"], ["linkedin", "LinkedIn"]]) {
      const url = ((sm[key] || {}).url || "").trim();
      if (url && !/^https:\/\/[^\s]+\.[^\s]+$/.test(url)) {
        toast.error((de ? "Ungültige HTTPS-URL für " : "Invalid HTTPS URL for ") + label);
        return;
      }
    }
    try { await api.put("/settings", s); toast.success("OK"); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const de = lang === "de";
  const faviconSrc = s.favicon_version ? `${API}/branding/favicon?v=${encodeURIComponent(s.favicon_version)}` : "/favicon.ico";
  const uploadFavicon = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) { toast.error(de ? "Favicon zu groß (max. 1 MB)" : "Favicon too large (max 1 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/admin/favicon", { data: reader.result, mime: file.type });
        setS({ ...s, favicon_version: data.favicon_version, favicon_mime: data.favicon_mime });
        applyFavicon(data.favicon_version);
        toast.success(de ? "Favicon aktualisiert" : "Favicon updated");
      } catch (err) { toast.error(apiErr(err)); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const resetFavicon = async () => {
    try {
      await api.delete("/admin/favicon");
      setS({ ...s, favicon_version: null, favicon_mime: null });
      const link = document.querySelector('link[rel="icon"]');
      if (link) link.href = "/favicon.ico";
      toast.success(de ? "Auf Standard zurückgesetzt" : "Reset to default");
    } catch (err) { toast.error(apiErr(err)); }
  };
  const ogSrc = s.og_image_version ? `${API}/branding/og-image.png?v=${encodeURIComponent(s.og_image_version)}` : (s.og_image || "");
  const uploadOgImage = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error(de ? "Bild zu groß (max. 5 MB)" : "Image too large (max 5 MB)"); return; }
    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const { data } = await api.post("/admin/og-image", { data: reader.result, mime: file.type });
        setS({ ...s, og_image_version: data.og_image_version, og_image_mime: data.og_image_mime });
        toast.success(de ? "OG-Bild aktualisiert" : "OG image updated");
      } catch (err) { toast.error(apiErr(err)); }
    };
    reader.readAsDataURL(file);
    e.target.value = "";
  };
  const resetOgImage = async () => {
    try {
      await api.delete("/admin/og-image");
      setS({ ...s, og_image_version: null, og_image_mime: null });
      toast.success(de ? "Auf Standard zurückgesetzt" : "Reset to default");
    } catch (err) { toast.error(apiErr(err)); }
  };
  return (
    <div className="max-w-3xl space-y-6">
      <div className="flex items-center justify-between"><h1 className="font-display text-2xl font-black">{t("admin.settings")}</h1><button onClick={save} className="btn-primary" data-testid="settings-save">{t("common.save")}</button></div>

      <Section title={de ? "Firmendaten" : "Company"}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Name"><Input testid="s-name" value={s.company_name || ""} onChange={(e) => set("company_name", e.target.value)} /></Field>
          <Field label="E-Mail"><Input testid="s-email" value={s.company_email || ""} onChange={(e) => set("company_email", e.target.value)} /></Field>
          <Field label="Telefon"><Input testid="s-phone" value={s.company_phone || ""} onChange={(e) => set("company_phone", e.target.value)} /></Field>
          <Field label="USt-IdNr."><Input testid="s-vat" value={s.vat_id || ""} onChange={(e) => set("vat_id", e.target.value)} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Adresse"><Input testid="s-address" value={s.company_address || ""} onChange={(e) => set("company_address", e.target.value)} /></Field>
          <Field label={de ? "Webseite" : "Website"}><Input testid="s-website" value={s.company_website || ""} onChange={(e) => set("company_website", e.target.value)} placeholder="https://jonuzovicdesign.de" /></Field>
        </div>
        <Field label="Bankdaten"><Input testid="s-bank" value={s.bank_info || ""} onChange={(e) => set("bank_info", e.target.value)} /></Field>
      </Section>

      <Section title={de ? "Soziale Medien" : "Social Media"}>
        <p className="text-sm text-muted-foreground">{de ? "Aktivierte Plattformen mit gültiger HTTPS-URL erscheinen im Footer. Leere oder deaktivierte Plattformen werden ausgeblendet." : "Enabled platforms with a valid HTTPS URL appear in the footer. Empty or disabled platforms are hidden."}</p>
        {[["facebook", "Facebook"], ["instagram", "Instagram"], ["linkedin", "LinkedIn"]].map(([key, label]) => {
          const e = (s.social_media || {})[key] || {};
          return (
            <div key={key} className="grid sm:grid-cols-[170px_1fr] gap-3 sm:gap-4 sm:items-center">
              <div className="flex items-center gap-3">
                <Switch checked={!!e.enabled} onCheckedChange={(v) => setSocial(key, { enabled: v })} data-testid={`social-${key}-toggle`} />
                <span className="text-sm font-medium">{label}</span>
              </div>
              <Field label={de ? "Profil-URL" : "Profile URL"}>
                <Input testid={`social-${key}-url`} value={e.url || ""} onChange={(ev) => setSocial(key, { url: ev.target.value })} placeholder={`https://${key}.com/...`} />
              </Field>
            </div>
          );
        })}
      </Section>

      <Section title="Favicon">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
            <img src={faviconSrc} alt="favicon" className="h-10 w-10 object-contain" data-testid="favicon-preview" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
          </div>
          <div className="text-sm text-muted-foreground flex-1">
            {de ? "Aktuelles Browser-Tab-Icon. PNG, ICO, SVG, JPG oder WEBP (max. 1 MB)." : "Current browser-tab icon. PNG, ICO, SVG, JPG or WEBP (max 1 MB)."}
          </div>
        </div>
        <input ref={favInput} type="file" accept=".png,.ico,.svg,.jpg,.jpeg,.webp,image/png,image/x-icon,image/svg+xml,image/jpeg,image/webp" className="hidden" onChange={uploadFavicon} data-testid="favicon-file-input" />
        <div className="flex gap-2">
          <button type="button" onClick={() => favInput.current?.click()} className="btn-primary !py-2 !px-4 text-sm" data-testid="favicon-upload-btn">{de ? "Favicon hochladen" : "Upload favicon"}</button>
          {s.favicon_version && <button type="button" onClick={resetFavicon} className="btn-ghost !py-2 !px-4 text-sm" data-testid="favicon-reset-btn">{de ? "Auf Standard zurücksetzen" : "Reset to default"}</button>}
        </div>
      </Section>

      <Section title="Hero (Homepage)">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Titel DE"><Input testid="s-hero-de" value={s.hero_title_de || ""} onChange={(e) => set("hero_title_de", e.target.value)} /></Field>
          <Field label="Titel EN"><Input testid="s-hero-en" value={s.hero_title_en || ""} onChange={(e) => set("hero_title_en", e.target.value)} /></Field>
          <Field label="Subtitel DE"><Input testid="s-sub-de" value={s.hero_subtitle_de || ""} onChange={(e) => set("hero_subtitle_de", e.target.value)} /></Field>
          <Field label="Subtitel EN"><Input testid="s-sub-en" value={s.hero_subtitle_en || ""} onChange={(e) => set("hero_subtitle_en", e.target.value)} /></Field>
        </div>
      </Section>

      <Section title={lang === "de" ? "Statistiken" : "Statistics"}>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {["projects", "clients", "years", "satisfaction"].map((k) => (
            <Field key={k} label={k}><Input testid={`s-stat-${k}`} type="number" value={(s.stats || {})[k] ?? ""} onChange={(e) => setStat(k, e.target.value)} /></Field>
          ))}
        </div>
      </Section>

      <Section title={lang === "de" ? "Rechnungen" : "Invoices"}>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Präfix"><Input testid="s-inv-prefix" value={s.invoice_prefix || ""} onChange={(e) => set("invoice_prefix", e.target.value)} /></Field>
          <Field label={lang === "de" ? "Nächste Nummer" : "Next number"}><Input testid="s-inv-next" type="number" value={s.next_invoice_number ?? 1} onChange={(e) => set("next_invoice_number", Number(e.target.value))} /></Field>
        </div>
        <Field label="Fußzeile"><Textarea testid="s-inv-footer" value={s.invoice_footer || ""} onChange={(e) => set("invoice_footer", e.target.value)} /></Field>
      </Section>

      <Section title={lang === "de" ? "Rechtstexte (CMS)" : "Legal texts (CMS)"}>
        {["imprint", "privacy", "terms", "withdrawal"].map((k) => (
          <div key={k} className="grid sm:grid-cols-2 gap-4">
            <Field label={`${t(`legal.${k}`)} (DE)`}><Textarea testid={`s-legal-${k}-de`} value={s[`legal_${k}_de`] || ""} onChange={(e) => set(`legal_${k}_de`, e.target.value)} /></Field>
            <Field label={`${t(`legal.${k}`)} (EN)`}><Textarea testid={`s-legal-${k}-en`} value={s[`legal_${k}_en`] || ""} onChange={(e) => set(`legal_${k}_en`, e.target.value)} /></Field>
          </div>
        ))}
      </Section>

      <Section title="SEO">
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Meta-Titel DE"><Input testid="s-seo-title-de" value={s.seo_title_de || ""} onChange={(e) => set("seo_title_de", e.target.value)} /></Field>
          <Field label="Meta-Titel EN"><Input testid="s-seo-title-en" value={s.seo_title_en || ""} onChange={(e) => set("seo_title_en", e.target.value)} /></Field>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Meta-Beschreibung DE"><Textarea testid="s-seo-desc-de" value={s.seo_description_de || ""} onChange={(e) => set("seo_description_de", e.target.value)} /></Field>
          <Field label="Meta-Beschreibung EN"><Textarea testid="s-seo-desc-en" value={s.seo_description_en || ""} onChange={(e) => set("seo_description_en", e.target.value)} /></Field>
        </div>
        <Field label="Open-Graph Bild (Social Sharing)">
          <div className="flex items-center gap-4 mt-1">
            <div className="h-20 w-[10.5rem] rounded-lg border border-border bg-secondary flex items-center justify-center overflow-hidden shrink-0">
              {ogSrc
                ? <img src={ogSrc} alt="og" className="h-full w-full object-cover" data-testid="og-image-preview" onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                : <span className="text-xs text-muted-foreground px-2 text-center">1200×630</span>}
            </div>
            <div className="text-sm text-muted-foreground flex-1">
              {de ? "Vorschaubild beim Teilen (WhatsApp, LinkedIn, X …). PNG, JPG oder WEBP, empfohlen 1200×630 (max. 5 MB)." : "Preview image when sharing (WhatsApp, LinkedIn, X …). PNG, JPG or WEBP, recommended 1200×630 (max 5 MB)."}
            </div>
          </div>
          <input ref={ogInput} type="file" accept=".png,.jpg,.jpeg,.webp,image/png,image/jpeg,image/webp" className="hidden" onChange={uploadOgImage} data-testid="og-image-file-input" />
          <div className="flex gap-2 mt-3">
            <button type="button" onClick={() => ogInput.current?.click()} className="btn-primary !py-2 !px-4 text-sm" data-testid="og-image-upload-btn">{de ? "Bild hochladen" : "Upload image"}</button>
            {s.og_image_version && <button type="button" onClick={resetOgImage} className="btn-ghost !py-2 !px-4 text-sm" data-testid="og-image-reset-btn">{de ? "Entfernen" : "Remove"}</button>}
          </div>
        </Field>
        <p className="text-xs text-muted-foreground">Sitemap: <a className="text-primary underline" href="/api/seo/sitemap.xml" target="_blank" rel="noreferrer">/api/seo/sitemap.xml</a> · robots.txt: <a className="text-primary underline" href="/api/seo/robots.txt" target="_blank" rel="noreferrer">/api/seo/robots.txt</a></p>
      </Section>

      <GoogleOAuthSection />

      <Section title={de ? "KI-Assistent (Chatbot)" : "AI Assistant (Chatbot)"}>
        <Field label={de ? "Name des Assistenten" : "Assistant name"}><Input testid="ai-name" value={s.ai_name || ""} onChange={(e) => set("ai_name", e.target.value)} placeholder={`${s.company_name || "JonuzovicDesign"} Assistent`} /></Field>
        <Field label={de ? "Begrüßung (DE)" : "Greeting (DE)"}><Textarea value={s.ai_greeting_de || ""} onChange={(e) => set("ai_greeting_de", e.target.value)} rows={2} /></Field>
        <Field label={de ? "Begrüßung (EN)" : "Greeting (EN)"}><Textarea value={s.ai_greeting_en || ""} onChange={(e) => set("ai_greeting_en", e.target.value)} rows={2} /></Field>
        <p className="text-xs text-muted-foreground">{de ? "Name & Begrüßung mit „Speichern“ unten sichern. Anbieter, Schlüssel und Modell werden separat unten verwaltet." : "Save name & greeting with the button below. Provider, key and model are managed separately below."}</p>
      </Section>

      <AIProviderConfig />

      <Section title={lang === "de" ? "System & E-Mail" : "System & Email"}>
        {status && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm" data-testid="system-status">
            {[["E-Mail", status.email_mode], ["Provider", status.email_provider_configured ? "✓" : "—"], ["Storage", status.storage_mode], ["Stripe", status.stripe_mode]].map(([l, v]) => (
              <div key={l} className="surface p-3"><div className="text-muted-foreground text-xs">{l}</div><div className="font-semibold capitalize">{String(v)}</div></div>
            ))}
          </div>
        )}
        <button type="button" onClick={testEmail} className="btn-ghost !py-2 !px-4 text-sm" data-testid="test-email-btn">{lang === "de" ? "Test-E-Mail senden" : "Send test email"}</button>
        <div className="max-h-52 overflow-y-auto text-sm divide-y divide-border">
          {emailLog.slice(0, 15).map((e) => (
            <div key={e.id} className="py-2 flex justify-between gap-3">
              <span className="truncate">{e.subject}</span>
              <span className="text-muted-foreground shrink-0">{e.to} · {e.status}</span>
            </div>
          ))}
          {emailLog.length === 0 && <p className="text-muted-foreground py-2">{t("common.none")}</p>}
        </div>
      </Section>
    </div>
  );
}
